import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {filter, map, distinctUntilChanged} from 'rxjs/operators';
import {produce} from 'immer';

import * as APIM from '@common/utils/dist/models/api-messages.js';

import {Store} from './abstract-store';

function key(intent: APIM.EventIntent, pK: string): string {
  return `${intent}:${pK}`;
}

export interface EventMatchEntry {
  count: number;
  trackingId: string | null;
}

export interface EventMatchState {
  [key: string]: EventMatchEntry;
}

@Injectable({providedIn: 'root'})
export class EventMatchStore extends Store<EventMatchState> {
  constructor() {
    super({});
  }

  register(intent: APIM.EventIntent, pK: string): Observable<string> {
    const k = key(intent, pK);
    this._increment(k);
    return this.state$.pipe(
      map((state) => state[k]?.trackingId),
      filter((id): id is string => id != null),
      distinctUntilChanged(),
    );
  }

  deregister(intent: APIM.EventIntent, pK: string): void {
    this._decrement(key(intent, pK));
  }

  publish(intent: APIM.EventIntent, trackingId: string, pKs: string[]): void {
    this.setState(
      produce(this.state(), (draft) => {
        for (const pK of pKs) {
          const k = key(intent, pK);
          if (draft[k]?.count > 0) {
            draft[k].trackingId = trackingId;
          }
        }
      }),
    );
  }

  private _increment(k: string): void {
    this.setState(
      produce(this.state(), (draft) => {
        if (draft[k]) {
          draft[k].count++;
        } else {
          draft[k] = {count: 1, trackingId: null};
        }
      }),
    );
  }

  private _decrement(k: string): void {
    this.setState(
      produce(this.state(), (draft) => {
        if (!draft[k]) return;
        draft[k].count--;
        if (draft[k].count <= 0) {
          delete draft[k];
        }
      }),
    );
  }
}
